import React, { useEffect, useRef } from "react";
import CommunityPostCard from "./CommunityPostCard";

function emptyCopy(contextType) {
  switch (contextType) {
    case "creator":
      return {
        title: "Creator activity",
        body: "Announcements, shared videos, and updates from this creator will appear here.",
      };
    case "organization":
    case "org":
      return {
        title: "Ministry activity",
        body: "Announcements, updates, and media from this organization will appear here.",
      };
    case "project":
      return {
        title: "Project activity",
        body: "Milestone updates, progress, and campaign moments will appear here as the project moves forward.",
      };
    case "event":
    case "group":
    case "ministry":
      return {
        title: "Activity",
        body: "Updates for this community space will appear here when published.",
      };
    case "saved":
      return {
        title: "No saved posts",
        body: "Save posts from the Community feed to find them here later.",
      };
    case "profile":
      return {
        title: "No posts yet",
        body: "Posts shared by this Community member will appear here.",
      };
    default:
      return {
        title: "Community activity",
        body: "Be the first to share an update. Create a public handle, then post to the community feed.",
      };
  }
}

/**
 * Shared Activity Feed — My Feed, Community Profile, and future entity feeds.
 * Renders shared CommunityPostCard only (no duplicate post rendering).
 */
function ActivityFeed({
  contextType = "global",
  title = null,
  emptyTitle = null,
  emptyMessage = null,
  loading = false,
  error = false,
  items = null,
  onRetry = null,
  onItemUpdate = null,
  className = "",
  showRankingMeta = false,
  hasMore = false,
  loadingMore = false,
  onLoadMore = null,
}) {
  const list = Array.isArray(items) ? items : [];
  const empty = emptyCopy(contextType);
  const sentinelRef = useRef(null);

  useEffect(() => {
    if (!hasMore || typeof onLoadMore !== "function" || loadingMore || loading || error) {
      return undefined;
    }
    const node = sentinelRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      return undefined;
    }
    let cancelled = false;
    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries && entries[0];
        if (!cancelled && entry && entry.isIntersecting) {
          onLoadMore();
        }
      },
      { root: null, rootMargin: "240px 0px", threshold: 0 }
    );
    observer.observe(node);
    return () => {
      cancelled = true;
      observer.disconnect();
    };
  }, [hasMore, onLoadMore, loadingMore, loading, error, list.length]);

  if (loading) {
    return (
      <div
        className={`community-feed activity-feed ${className}`.trim()}
        role="status"
        aria-live="polite"
      >
        <div className="community-feed__skeleton" />
        <div className="community-feed__skeleton" />
        <div className="community-feed__skeleton community-feed__skeleton--short" />
      </div>
    );
  }

  if (error) {
    return (
      <div
        className={`community-feed community-feed--error activity-feed ${className}`.trim()}
        role="alert"
      >
        <h3 className="community-feed__heading">Couldn’t load this feed</h3>
        <p className="community-feed__body">
          Please try again in a moment.
        </p>
        <div className="community-feed__actions">
          {typeof onRetry === "function" ? (
            <button type="button" className="btn btn-danger" onClick={onRetry}>
              Retry
            </button>
          ) : null}
        </div>
      </div>
    );
  }

  if (list.length === 0) {
    return (
      <div
        className={`community-feed community-feed--empty activity-feed ${className}`.trim()}
      >
        <div className="community-feed__rail" aria-hidden="true">
          <span className="community-feed__rail-dot" />
          <span className="community-feed__rail-line" />
          <span className="community-feed__rail-dot" />
          <span className="community-feed__rail-line" />
          <span className="community-feed__rail-dot" />
        </div>
        <div>
          <h3 className="community-feed__heading">
            {emptyTitle || title || empty.title}
          </h3>
          <p className="community-feed__body">{emptyMessage || empty.body}</p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`community-feed community-feed--posts activity-feed ${className}`.trim()}
      aria-live="polite"
    >
      {title ? (
        <h3 className="activity-feed__title community-feed__heading">{title}</h3>
      ) : null}
      {list.map((item, index) => (
        <CommunityPostCard
          key={item.id || item.slug || `activity-${index}`}
          item={item}
          showRankingMeta={showRankingMeta}
          onReacted={(updated) => {
            if (typeof onItemUpdate === "function") {
              onItemUpdate(updated);
            }
          }}
          onDeleted={(postId) => {
            if (typeof onItemUpdate === "function") {
              onItemUpdate({ id: postId, _deleted: true });
            }
          }}
        />
      ))}
      {hasMore && typeof onLoadMore === "function" ? (
        <div className="activity-feed__more">
          <div
            ref={sentinelRef}
            className="activity-feed__sentinel"
            aria-hidden="true"
          />
          <button
            type="button"
            className="btn btn-outline-secondary activity-feed__more-btn"
            onClick={onLoadMore}
            disabled={loadingMore}
          >
            {loadingMore ? "Loading…" : "Load more"}
          </button>
        </div>
      ) : null}
    </div>
  );
}

export default ActivityFeed;
export { CommunityPostCard as ActivityPostCard };
